import { zodResolver } from '@hookform/resolvers/zod';
import { motion, useReducedMotion } from 'framer-motion';
import { AlertCircle, Lock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Logo } from '@/components/Logo';
import { ModeDot } from '@/components/ModeSwitcher';
import { useAuth } from '@/lib/auth/useAuth';
import type { AuthLoginError } from '@/lib/auth/types';
import { useMode } from '@/lib/mode/ModeProvider';
import type { OperationMode } from '@/lib/mode/setCurrentMode';

const LoginSchema = z.object({
  email: z.string().email('Enter a valid email address.'),
  password: z.string().min(8, 'Password must be at least 8 characters.'),
  mode: z.enum(['production', 'sandbox']),
});
type LoginForm = z.infer<typeof LoginSchema>;

export default function LoginPage() {
  const { login, isAuthenticated, isBootstrapping } = useAuth();
  const { mode: storedMode } = useMode();
  const navigate = useNavigate();
  const location = useLocation() as { state: { from?: string } | null };
  const reduceMotion = useReducedMotion();

  const [apiError, setApiError] = useState<AuthLoginError | null>(null);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(LoginSchema),
    // Default the env radio to whatever the tab remembers (or production for
    // first-time visitors). Phase B will hide the radio for users with only
    // one entitlement; until then we always show it.
    defaultValues: { email: '', password: '', mode: storedMode },
  });
  const selectedMode = watch('mode');

  useEffect(() => {
    if (!isBootstrapping && isAuthenticated) {
      navigate(location.state?.from ?? '/dashboard', { replace: true });
    }
  }, [isBootstrapping, isAuthenticated, location.state, navigate]);

  const onSubmit = handleSubmit(async ({ email, password, mode }) => {
    setApiError(null);
    // Login signature is mode-unaware today (Phase B will extend it). We
    // post the credentials through the existing hook; the chosen mode is
    // applied locally via ModeProvider so the post-login chrome reflects
    // the user's intent immediately. Once the backend accepts `mode`,
    // surface a 403 (auth.environment_forbidden) by re-reading the response.
    const err = await login(email, password);
    if (err) {
      setApiError(err);
      return;
    }
    // Persist the requested env in localStorage so the chip + banner + DOM
    // attribute land in the right state on first paint. The actual JWT
    // env-binding comes from Phase B — until then, the chip is advisory.
    try {
      window.localStorage.setItem('dwpay.mode', mode);
    } catch {
      /* ignore */
    }
    navigate(location.state?.from ?? '/dashboard', { replace: true });
  });

  if (!isBootstrapping && isAuthenticated) {
    return <Navigate to={location.state?.from ?? '/dashboard'} replace />;
  }

  return (
    <div className="grid min-h-screen place-items-center bg-background p-8">
      <motion.div
        className="flex w-full max-w-sm flex-col items-center"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={reduceMotion ? { duration: 0 } : { duration: 0.3, ease: 'easeOut' }}
      >
        <Logo className="mb-6 h-9" />
        <Card className="w-full">
          <CardHeader className="space-y-1">
            <div className="flex items-center gap-2 text-primary">
              <Lock className="h-4 w-4" />
              <CardTitle>Sign in</CardTitle>
            </div>
            <CardDescription>Payment Proxy admin — authorised users only.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  placeholder="admin@example.com"
                  {...register('email')}
                  aria-invalid={!!errors.email}
                />
                {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  {...register('password')}
                  aria-invalid={!!errors.password}
                />
                {errors.password && <p className="text-xs text-destructive">{errors.password.message}</p>}
              </div>

              <EnvironmentRadios
                selected={selectedMode}
                onChange={(next) => setValue('mode', next, { shouldDirty: true })}
              />

              {apiError && (
                <div
                  role="alert"
                  className={
                    apiError.kind === 'lockout'
                      ? 'flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-200'
                      : 'flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive'
                  }
                >
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                  <span>{apiError.message}</span>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting
                  ? 'Signing in…'
                  : `Sign in to ${selectedMode === 'sandbox' ? 'Sandbox' : 'Production'}`}
              </Button>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

interface EnvironmentRadiosProps {
  selected: OperationMode;
  onChange: (next: OperationMode) => void;
}

/**
 * Two-radio environment selector shown beneath the password field.
 * Phase B will hide this for users entitled to a single environment;
 * for now we always show both so admins can dry-run the chrome.
 */
function EnvironmentRadios({ selected, onChange }: EnvironmentRadiosProps) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-xs font-medium text-muted-foreground">Environment</legend>
      <div className="grid grid-cols-2 gap-2">
        <EnvironmentRadio
          mode="production"
          label="Production"
          checked={selected === 'production'}
          onSelect={onChange}
        />
        <EnvironmentRadio
          mode="sandbox"
          label="Sandbox"
          checked={selected === 'sandbox'}
          onSelect={onChange}
        />
      </div>
    </fieldset>
  );
}

function EnvironmentRadio({
  mode,
  label,
  checked,
  onSelect,
}: {
  mode: OperationMode;
  label: string;
  checked: boolean;
  onSelect: (next: OperationMode) => void;
}) {
  return (
    <label
      className={
        checked
          ? 'flex cursor-pointer items-center gap-2 rounded-md border border-primary/70 bg-primary/5 px-3 py-2 text-sm font-medium text-foreground transition-colors'
          : 'flex cursor-pointer items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent/40'
      }
    >
      <input
        type="radio"
        name="login-mode"
        value={mode}
        checked={checked}
        onChange={() => onSelect(mode)}
        className="sr-only"
      />
      <ModeDot mode={mode} />
      <span>{label}</span>
    </label>
  );
}
